<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import AsanaMark from 'dashboard/components/widgets/conversation/asana/AsanaMark.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete']);

const accountId = useMapGetter('getCurrentAccountId');

const isTask = computed(() => props.type === 'tasks');
const title = computed(() => (isTask.value ? props.item.name : props.item.title));
const secondaryLabel = computed(() => {
  if (isTask.value) {
    return props.item.conversation?.assignee?.name
      ? `Assigned to ${props.item.conversation.assignee.name}`
      : 'Unassigned conversation';
  }

  return props.item.assignee?.name ? `Assigned to ${props.item.assignee.name}` : 'Reminder';
});
const statusLabel = computed(() => {
  if (isTask.value) return props.item.completed ? 'Done' : 'In progress';
  return props.item.status || 'scheduled';
});

const dateLabel = computed(() => {
  const value = isTask.value ? props.item.due_on : props.item.remind_at;
  if (!value) return '';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: isTask.value ? undefined : 'numeric',
    minute: isTask.value ? undefined : '2-digit',
  }).format(new Date(isTask.value ? `${value}T00:00:00` : value));
});

const conversationPath = computed(() => {
  const displayId = props.item.conversation?.display_id;
  if (!displayId) return '';
  return `/app/accounts/${accountId.value}/conversations/${displayId}`;
});
</script>

<template>
  <article
    class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-sm transition hover:border-n-slate-5 hover:bg-n-solid-2"
  >
    <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2">
      <AsanaMark v-if="isTask" small />
      <span v-else class="i-lucide-clock-3 size-5 text-n-brand" />
    </div>

    <div class="min-w-0 flex-1">
      <div class="mb-1 flex min-w-0 items-center gap-2">
        <p class="m-0 truncate text-sm font-medium text-n-slate-12">
          {{ title }}
        </p>
        <span
          class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize"
          :class="isTask && item.completed ? 'bg-n-teal-3 text-n-teal-10' : 'bg-n-blue-3 text-n-blue-10'"
        >
          {{ statusLabel }}
        </span>
      </div>

      <p class="m-0 truncate text-xs font-medium text-n-slate-11">
        {{ secondaryLabel }}
      </p>

      <p class="m-0 mt-1 truncate text-xs text-n-slate-10">
        <span v-if="item.company_name">{{ item.company_name }}</span>
        <span v-if="item.contact?.name"> · {{ item.contact.name }}</span>
        <span v-if="dateLabel"> · {{ isTask ? 'Due' : 'At' }} {{ dateLabel }}</span>
        <span v-if="item.conversation?.display_id">
          · Conversation #{{ item.conversation.display_id }}
        </span>
      </p>

      <p v-if="!isTask && item.description" class="m-0 mt-1 line-clamp-2 text-xs text-n-slate-10">
        {{ item.description }}
      </p>
    </div>

    <div class="flex shrink-0 items-center gap-1">
      <a
        v-if="conversationPath"
        :href="conversationPath"
        class="flex size-8 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
        title="Open conversation"
      >
        <span class="i-lucide-message-circle text-sm text-n-slate-10" />
      </a>
      <button
        type="button"
        class="flex size-8 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :title="isTask ? 'Edit task' : 'Edit reminder'"
        @click="emit('edit', item)"
      >
        <span class="i-lucide-pencil text-sm text-n-slate-10" />
      </button>
      <a
        v-if="isTask && item.permalink_url"
        :href="item.permalink_url"
        target="_blank"
        rel="noopener noreferrer"
        class="flex size-8 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
        title="Open in Asana"
        @click.stop
      >
        <span class="i-lucide-external-link text-sm text-n-slate-10" />
      </a>
      <button
        v-if="!isTask"
        type="button"
        class="flex size-8 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-10"
        title="Delete reminder"
        @click="emit('delete', item)"
      >
        <span class="i-lucide-trash-2 text-sm" />
      </button>
    </div>
  </article>
</template>
